import { useState } from 'react';

import {
  MissingTimesheetShift,
  MissingTimesheetWorker,
  QueueFilterValues,
  QueuePage,
} from '../api/types';
import MissingTimesheetShiftCard from '../components/MissingTimesheetShiftCard';
import ResolveTimesheetDialog from '../components/ResolveTimesheetDialog';
import QueueError from '../components/QueueError';
import QueuePagination from '../components/QueuePagination';
import QueueFilterHeader from '../components/QueueFilterHeader';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useMissingTimesheetQueue from '../hooks/useMissingTimesheetQueue';
import useQueuePageClamp from '../hooks/useQueuePageClamp';

import LoadingState from '@/components/LoadingState';

// Missing-timesheet queue: hired workers on ended shifts with no captured
// timesheet data, grouped by shift. Mirrors the No-show layout.
//
// Each worker row also carries its own "Enter hours" action and contact link
// (GRAVY-10307): resolution is worker-scoped, so a shift-level action could not
// say which of several missing workers it meant.
//
// A card's chevron opens the shared Shift Detail panel over the queue
// (GRAVY-10297, moved off a separate text button by GRAVY-10318). Selection
// lives here, not in the card, so the list, its scroll
// position and the loaded data stay mounted underneath and closing returns to
// exactly the same view — the same arrangement Calendar and Needs Approval use,
// against the same shift-scoped contract.

type Props = {
  agencyId: number;
  /** Owned by the page, so it survives tab switches (GRAVY-10303). */
  filters: QueueFilterValues;
  onFiltersChange: (next: QueueFilterValues) => void;
  /** Also owned by the page, for the same reason (GRAVY-10304). */
  page: QueuePage;
  onPageChange: (next: number) => void;
};

const MissingTimesheetTab = ({
  agencyId,
  filters,
  onFiltersChange,
  page,
  onPageChange,
}: Props) => {
  const { data, isLoading, isError, refetch } = useMissingTimesheetQueue(
    agencyId,
    filters,
    page,
  );
  const { data: filterOptions } = useDashboardFilters(agencyId);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  // The worker being resolved, with the shift it belongs to. Held as a pair
  // because a worker row is only actionable together with its occurrence — the
  // same worker can be missing a timesheet on several shifts (GRAVY-10307).
  const [resolving, setResolving] = useState<{
    shift: MissingTimesheetShift;
    worker: MissingTimesheetWorker;
  } | null>(null);
  // A live refresh can shrink the queue under an admin working a later page,
  // stranding them on a page that no longer exists (GRAVY-10323).
  useQueuePageClamp(data?.pagination, page.page, onPageChange);

  const shifts = data?.shifts ?? [];

  return (
    <div>
      <QueueFilterHeader
        heading="Missing timesheet"
        queue="missing_timesheet"
        options={filterOptions}
        value={filters}
        onChange={onFiltersChange}
      />

      {isLoading && !data ? (
        <LoadingState />
      ) : isError ? (
        <QueueError
          message="Unable to load the missing-timesheet queue."
          onRetry={refetch}
        />
      ) : shifts.length === 0 ? (
        <div className="dashv2-queue-empty">
          {data && data.total > 0
            ? 'No results on this page.'
            : 'No missing timesheets. 🎉'}
        </div>
      ) : (
        <div className="dashv2-queue-list">
          {shifts.map((shift) => (
            <MissingTimesheetShiftCard
              key={shift.shift_id}
              shift={shift}
              onSelect={setSelectedShiftId}
              onResolveWorker={(worker) => setResolving({ shift, worker })}
            />
          ))}
        </div>
      )}

      {data?.pagination && !isError && (
        <QueuePagination
          pagination={data.pagination}
          onPageChange={onPageChange}
          queue="Missing timesheet"
        />
      )}

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}

      {/* Rendered over the queue, which stays mounted — so cancelling returns
          to the same tab, scroll position and page. */}
      {resolving && (
        <ResolveTimesheetDialog
          shift={resolving.shift}
          worker={resolving.worker}
          onClose={() => setResolving(null)}
        />
      )}
    </div>
  );
};

export default MissingTimesheetTab;
